import { usePage, router } from "@inertiajs/react"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import { setCsrfToken } from "@/lib/csrf"

type Flash = {
  notice?: string | null
  alert?: string | null
}

type SharedProps = {
  flash?: Flash
  csrfToken?: string | null
}

function SyncCsrfToken() {
  const { props } = usePage<SharedProps>()
  useEffect(() => {
    if (props.csrfToken) setCsrfToken(props.csrfToken)
  }, [props.csrfToken])
  return null
}

function FlashFromServer() {
  const { url, version, props } = usePage<SharedProps>()
  const flash = props.flash
  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    return router.on("start", () => {
      // 每次新请求都允许再次展示相同 flash（例如连续添加重复问句）
      lastKey.current = null
    })
  }, [])

  useEffect(() => {
    const notice = flash?.notice?.trim() ?? ""
    const alert = flash?.alert?.trim() ?? ""
    if (!notice && !alert) {
      lastKey.current = null
      return
    }

    // Same URL + same copy without a version bump would wrongly suppress re-shows
    // (e.g. resending OTP with identical notice text).
    const key = `${String(version)}::${url}::${notice}::${alert}`
    if (lastKey.current === key) return
    lastKey.current = key

    if (notice) toast.success(notice)
    if (alert) toast.error(alert)
  }, [version, url, flash?.notice, flash?.alert])

  return null
}

type Props = { children: ReactNode }

export default function InertiaRootLayout({ children }: Props) {
  return (
    <>
      <SyncCsrfToken />
      <FlashFromServer />
      <Toaster />
      {children}
    </>
  )
}
